import React, { useState } from 'react';
import { Box, Text, Button, Image, VStack } from '@chakra-ui/react';
import { validatePhotoSize, compressImage } from '../utils';
import { MAX_PHOTOS_ALLOWED, MIN_PHOTOS_REQUIRED, UserType, USER_TYPES, IMAGE_COMPRESSION } from '../constants';

interface PhotoUploadSectionProps {
  uploadedPhotos: File[];
  setUploadedPhotos: (photos: File[]) => void;
  photoUrls: string[];
  setPhotoUrls: (urls: string[]) => void;
  maxPhotoSize: number;
  title?: string;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  buttonText?: string;
  showTips?: boolean;
  tipsContext?: UserType;
}

const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({
  uploadedPhotos,
  setUploadedPhotos,
  photoUrls,
  setPhotoUrls,
  maxPhotoSize,
  title = "Make an impression with your photos, upload your best shots!*",
  emptyStateTitle = "Upload photos",
  emptyStateSubtitle = `Upload at least ${MIN_PHOTOS_REQUIRED} photos`,
  buttonText = "Add more photos",
  showTips = false,
  tipsContext = USER_TYPES.INFLUENCER
}) => {
  const [error, setError] = useState<string | null>(null);

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // Clear input immediately

    if (files.length > 0) {
      // Validate file sizes (original files, before compression)
      try {
        files.forEach(file => validatePhotoSize(file, maxPhotoSize));
      } catch (err: any) {
        setError(err.message);
        return;
      }

      const maxPhotos = MAX_PHOTOS_ALLOWED;
      const remainingSlots = maxPhotos - uploadedPhotos.length;
      if (remainingSlots <= 0) return;

      const filesToAdd = files.slice(0, remainingSlots);

      // Compress photos before storing
      const compressedFiles = await Promise.all(
        filesToAdd.map(file => compressImage(file, IMAGE_COMPRESSION))
      );

      const newPhotos = [...uploadedPhotos, ...compressedFiles].slice(0, MAX_PHOTOS_ALLOWED);
      setUploadedPhotos(newPhotos);

      // Create URLs for preview (use compressed files)
      const newUrls = compressedFiles.map(file => URL.createObjectURL(file));
      setPhotoUrls([...photoUrls, ...newUrls].slice(0, maxPhotos));
    }
  };

  const handlePhotoDelete = (index: number) => {
    const newPhotos = uploadedPhotos.filter((_, i) => i !== index);
    const newUrls = photoUrls.filter((_, i) => i !== index);
    setUploadedPhotos(newPhotos);
    setPhotoUrls(newUrls);
  };

  const triggerFileInput = (inputId: string) => {
    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <Box width="100%">
      <Box
        className='form-container-photo-upload'
      >
      <Text
        className='subtitle-text'
        marginBottom="16px"
      >
        {title}
      </Text>

      {error && (
        <Text
          color="#FF6B6B"
          fontSize="14px"
          marginBottom="12px"
        >
          {error}
        </Text>
      )}

      {uploadedPhotos.length === 0 ? (
        /* Upload Area - Empty State */
        <Box
          width="100%"
          height="200px"
          border="2px dashed #595C5D"
          borderRadius="12px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          _hover={{
            borderColor: "#C6D959",
            backgroundColor: "rgba(198, 217, 89, 0.05)"
          }}
          onClick={() => triggerFileInput('photo-upload')}
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
            id="photo-upload"
          />
          <Box width="120px" height="120px" marginBottom="6px">
            <Image
              src="/assets/images/download-img.png"
              alt="Upload photos"
              width="120px"
              height="120px"
            />
          </Box>
          <Text
            className='subtitle-text'
            marginBottom="4px"
          >
            {emptyStateTitle}
          </Text>
          <Text
            className='static-text'
          >
            {emptyStateSubtitle}
          </Text>
        </Box>
      ) : (
        /* Uploaded Photos - Grid View */
        <>
          {/* Status Text */}
          <Text
            className='subtitle-text'
            color="#C6D959 !important"
            marginBottom="16px"
          >
            {uploadedPhotos.length} photos Uploaded
          </Text>

          {/* Photo Grid */}
          <Box
            width="100%"
            display="grid"
            gridTemplateColumns="1fr 1fr"
            gap="17px"
            marginBottom="16px"
          >
            {photoUrls.map((url, index) => (
              <Box
                key={index}
                position="relative"
                width="140px"
                height="140px"
                borderRadius="18px"
                overflow="hidden"
                backgroundColor="#2A2A2A"
              >
                <Image
                  src={url}
                  alt={`Uploaded photo ${index + 1}`}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
                {/* Delete Button */}
                <Box
                  position="absolute"
                  top="8px"
                  right="8px"
                  width="28px"
                  height="28px"
                  backgroundColor="rgba(0, 0, 0, 0.7)"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  onClick={() => handlePhotoDelete(index)}
                  _hover={{
                    backgroundColor: "rgba(0, 0, 0, 0.9)"
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Add More Photos Button */}
          <Box width="100%" display="flex" justifyContent="center">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
              style={{ display: 'none' }}
              id="add-more-photos"
            />
            <Button
              className='button-big'
              cursor="pointer"
              isDisabled={uploadedPhotos.length >= MAX_PHOTOS_ALLOWED}
              onClick={() => {
                if (uploadedPhotos.length < MAX_PHOTOS_ALLOWED) {
                  triggerFileInput('add-more-photos');
                }
              }}
            >
              {buttonText}
            </Button>
          </Box>
        </>
      )}
      </Box>

      {/* Photo Tips Section */}
      {showTips && (
        <Box
          width="269px"
          height="164px"
          paddingLeft="18px"
        >
          <Text
            className='subtitle-text'
            marginBottom="20px"
          >
            Some tips for your photos
          </Text>

          <VStack spacing="16px" align="flex-start">
            <Text
              className='static-text'
            >
              Good image quality - no heavy filters
            </Text>
            <Text
              className='static-text'
            >
              {tipsContext === USER_TYPES.BUSINESS
                ? "Make sure the photos are taken with good lighting"
                : "Make sure your face is clearly visible"
              }
            </Text>
            <Text
              className='static-text'
            >
              {tipsContext === USER_TYPES.BUSINESS
                ? "Clear and high-quality photos will help you get more applications for your event"
                : "Upload your best shots"
              }
            </Text>
            {tipsContext === USER_TYPES.INFLUENCER && (
              <Text
                className='static-text'
              >
                Clear and nice pictures will help you access more events
              </Text>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default PhotoUploadSection;

